/**
 * CopyableReference (ALL-4857, D636): the one canonical source for showing a
 * support reference with one-click copy.
 *
 * Two variants share the same markup contract and CSS
 * (copyable-reference.css, which uses logical properties only):
 *   full:     "Support reference" label, the reference, a "Copy reference" button
 *   compact:  "Ref: ABC123 [copy]" for inline errors, toasts and success lines
 *
 * Accessibility: the copy button carries an aria-label, and a polite live
 * region announces "Copied" after a successful copy, or the copy-failed hint.
 * The reference itself is rendered with dir="ltr" so an opaque id reads the
 * same way inside an RTL locale.
 *
 * When there is no reference, nothing is rendered. A missing reference is the
 * normal state until the Spring change that adds the header to every response
 * lands, and a placeholder or client-made id would send support after a
 * request the server never recorded.
 *
 * Stacks: enterprise-wallet and admin-portal get this file as plain JS from
 * scripts/sync-error-page.mjs and use renderCopyableReferenceHtml plus
 * bindCopyableReferences. sandbox-shell gets it verbatim and its React
 * adapter, src/components/ui/copyable-reference.tsx, renders the same markup
 * and calls the same copyToClipboard, so the copy logic exists once.
 */

export const COPYABLE_REFERENCE_CLASS = "alloy-ref";
export const COPIED_RESET_MS = 2000;

export interface CopyableReferenceLabels {
  referenceLabel: string;
  referenceShortLabel: string;
  copyReference: string;
  copyShort: string;
  copyReferenceAria: string;
  copyReferenceCopied: string;
  copiedAnnouncement: string;
  copyFailed: string;
  missingCapability: string;
}

export const COPYABLE_REFERENCE_LABEL_KEYS = [
  "referenceLabel",
  "referenceShortLabel",
  "copyReference",
  "copyShort",
  "copyReferenceAria",
  "copyReferenceCopied",
  "copiedAnnouncement",
  "copyFailed",
  "missingCapability",
];

export type UiCatalog = Record<string, string>;

/** Picks the CopyableReference labels out of an error-locale `ui` catalog. */
export function copyableReferenceLabels(ui: UiCatalog): CopyableReferenceLabels {
  return {
    referenceLabel: ui.referenceLabel,
    referenceShortLabel: ui.referenceShortLabel,
    copyReference: ui.copyReference,
    copyShort: ui.copyShort,
    copyReferenceAria: ui.copyReferenceAria,
    copyReferenceCopied: ui.copyReferenceCopied,
    copiedAnnouncement: ui.copiedAnnouncement,
    copyFailed: ui.copyFailed,
    missingCapability: ui.missingCapability,
  };
}

/** Copies text to the clipboard and reports whether it worked. Falls back to
 * a hidden textarea and execCommand when the async Clipboard API is missing
 * or refused, such as in an insecure context or a denied permission. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the execCommand path
  }
  if (typeof document === "undefined" || !document.body) return false;
  const el = document.createElement("textarea");
  el.value = text;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(el);
  }
}

export function escapeHtml(value: unknown): string {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Markup for one reference. Returns "" when there is no reference. The
 * reference is escaped for both the attribute and the text node. */
export function renderCopyableReferenceHtml(reference: string | null | undefined, labels: CopyableReferenceLabels, compact: boolean): string {
  if (!reference) return "";
  const variant = compact ? "compact" : "full";
  const label = compact ? `${labels.referenceShortLabel}:` : labels.referenceLabel;
  const buttonText = compact ? labels.copyShort : labels.copyReference;
  const ref = escapeHtml(reference);
  return [
    `<span class="${COPYABLE_REFERENCE_CLASS} ${COPYABLE_REFERENCE_CLASS}--${variant}" data-alloy-ref="${ref}">`,
    `<span class="${COPYABLE_REFERENCE_CLASS}__label">${escapeHtml(label)}</span>`,
    `<code class="${COPYABLE_REFERENCE_CLASS}__value" dir="ltr">${ref}</code>`,
    `<button type="button" class="${COPYABLE_REFERENCE_CLASS}__copy" data-alloy-ref-copy aria-label="${escapeHtml(labels.copyReferenceAria)}" data-label="${escapeHtml(buttonText)}" data-copied-label="${escapeHtml(labels.copyReferenceCopied)}">${escapeHtml(buttonText)}</button>`,
    `<span class="${COPYABLE_REFERENCE_CLASS}__status" role="status" aria-live="polite"></span>`,
    `</span>`,
  ].join("");
}

/** Markup for the localized "Missing capability: <name>" line. Returns ""
 * when no capability was given. */
export function renderMissingCapabilityHtml(capability: string | null | undefined, labels: CopyableReferenceLabels): string {
  if (!capability) return "";
  const parts = labels.missingCapability.split("{capability}");
  const before = escapeHtml(parts[0] || "");
  const after = escapeHtml(parts.slice(1).join(""));
  return `<p class="${COPYABLE_REFERENCE_CLASS}-capability">${before}<code dir="ltr">${escapeHtml(capability)}</code>${after}</p>`;
}

/** Wires every copy button under root with one delegated listener. Safe to
 * call after every re-render: the listener is attached once per root. */
export function bindCopyableReferences(root: HTMLElement | null, labels: CopyableReferenceLabels): void {
  if (!root || root.dataset.alloyRefBound === "true") return;
  root.dataset.alloyRefBound = "true";
  root.addEventListener("click", function onCopyClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const button = target.closest("[data-alloy-ref-copy]");
    if (!button) return;
    const container = button.closest("[data-alloy-ref]");
    if (!container) return;
    const status = container.querySelector("[role=status]");
    const reference = container.getAttribute("data-alloy-ref") || "";
    void copyToClipboard(reference).then(function onCopied(ok) {
      if (status) status.textContent = ok ? labels.copiedAnnouncement : labels.copyFailed;
      if (!ok) return;
      button.setAttribute("data-copied", "true");
      button.textContent = button.getAttribute("data-copied-label") || labels.copyReferenceCopied;
      setTimeout(function resetCopied() {
        button.removeAttribute("data-copied");
        button.textContent = button.getAttribute("data-label") || labels.copyReference;
        if (status) status.textContent = "";
      }, COPIED_RESET_MS);
    });
  });
}
